import { Box } from "@mui/material"
import { useMemo } from "react"
import { useLocation, useParams } from "react-router"
import { Repeat } from "lib/@components/repeat"
import { If } from "lib/@components/switch"
import LoadingScreen from "minimals-template/components/LoadingScreen"
import { IS_LOADING } from "lib/graphql/return-is-loading-on-each-request"
import { withInitialValue } from "lib/graphql/with-initial-value"
import palette from "theme/palette"
import { refreshOnlyFor } from "lib/graphql/api"
import { createDraftId } from "authoring/routes/project/lib/project-utils"
import { getAuthoringPdfExportData } from "./controller/get-authoring-pdf-export-data"
import { PdfCoverHeader } from "./components/pdf-cover-header"
import { PdfComparisonTable } from "./components/pdf-comparison-table"
import { PdfPageError } from "./components/pdf-page-error"
import { PdfReferencesSection } from "./components/pdf-references-section"
import { PdfRichTextSection } from "./components/pdf-rich-text-section"
import { PdfTaskComparisonSection } from "./components/pdf-task-comparison-section"
import { PdfTaskOrderChangesSection } from "./components/pdf-task-order-changes-section"
import { PdfTimingSummary } from "./components/pdf-timing-summary"
import { PdfScheduleMetadataSection } from "./components/pdf-schedule-metadata-section"
import { sanitizePdfHtml } from "./components/pdf-formatters"
import { buildViewData } from "./export-schedule-pdf-page-helpers"
import { AMENDS_AUDIENCE_INTERNAL } from "../project/amends/lib/audience-aware-amends.mjs"

export function ExportSchedulePdfPage() {
    const { projectId, scheduleId } = useParams()
    const { search } = useLocation()
    const mode = useMemo(() => new URLSearchParams(search).get("mode") || "differences", [search])
    const audience = useMemo(() => new URLSearchParams(search).get("audience") || AMENDS_AUDIENCE_INTERNAL, [search])
    const { data: exportData, error } =
        getAuthoringPdfExportData.useResults.status(
            { projectId, scheduleId, audience },
            refreshOnlyFor(createDraftId(projectId, scheduleId)),
            withInitialValue(IS_LOADING)
        ) ?? {}
    const view = useMemo(() => buildViewData(exportData === IS_LOADING ? null : exportData, mode), [exportData, mode])

    if (exportData === IS_LOADING) return <LoadingScreen />
    if (error) return <PdfPageError message={error.message || "Failed to load authoring PDF export data."} />
    if (!exportData?.draft || !exportData?.original) {
        return <PdfPageError message="Authoring PDF export data is incomplete." />
    }

    const leadingSummaryHtml = sanitizePdfHtml(view.leadingSummary)
    const originalIntroHtml = sanitizePdfHtml(view.original.intro?.content)
    const draftIntroHtml = sanitizePdfHtml(view.draft.intro?.content)
    const originalNoteHtml = sanitizePdfHtml(view.original.intro?.note)
    const draftNoteHtml = sanitizePdfHtml(view.draft.intro?.note)

    return (
        <>
            <style>
                {`
                    @import url('https://fonts.googleapis.com/css2?family=Urbanist:ital,wght@0,400;0,700;1,400;1,700&display=swap');

                    @page {
                        size: A4 landscape;
                        margin: 0;
                    }
                    .pdf-ready,
                    .pdf-ready * {
                        font-family: Urbanist, Arial, Helvetica, sans-serif !important;
                        font-variant-ligatures: none !important;
                        -webkit-font-variant-ligatures: none !important;
                        font-feature-settings: "liga" 0, "clig" 0, "dlig" 0, "hlig" 0, "calt" 0 !important;
                        -webkit-font-feature-settings: "liga" 0, "clig" 0, "dlig" 0, "hlig" 0, "calt" 0 !important;
                        font-kerning: none !important;
                        font-synthesis: none !important;
                    }
                `}
            </style>
            <Box
                className="pdf-ready"
                sx={{
                    width: "100%",
                    bgcolor: palette.light.background.paper,
                    color: palette.light.text.primary,
                    p: "32px 32px 36px",
                    fontSize: "10px",
                    lineHeight: 1.6,
                    "@media print": { p: "32px 32px 36px" },
                }}
            >
                <Box>
                    <PdfCoverHeader
                        title={view.draft.title}
                        code={view.draft.code}
                        versionInfo={view.versionInfo}
                        summary={view.summary}
                        titleNote={view.titleNote}
                    />

                    <PdfTimingSummary
                        originalTasks={view.original.tasks}
                        draftTasks={view.draft.tasks}
                        draftTaskIndexLookup={view.draftTaskIndexLookup}
                    />

                    <If value={!!view.leadingSummary}>
                        <Box sx={{ mb: "24px", pageBreakAfter: "always", breakAfter: "page" }}>
                            <Box
                                sx={{
                                    fontWeight: 700,
                                    fontSize: "11px",
                                    color: palette.light.text.primary,
                                    mb: "10px",
                                }}
                            >
                                Summary of Changes
                            </Box>
                            <Box
                                className="styled-bullets"
                                sx={{
                                    fontSize: "10px",
                                    lineHeight: 1.7,
                                    color: palette.light.text.primary,
                                    "& p": { m: 0, mb: "8px" },
                                    "& ul, & ol": { my: "8px", pl: "18px" },
                                    "& li": { mb: "4px" },
                                }}
                                dangerouslySetInnerHTML={{ __html: leadingSummaryHtml }}
                            />
                        </Box>
                    </If>
                </Box>

                <PdfComparisonTable originalVersion={view.original.version} draftVersion={view.comparisonDraftVersion}>
                    <If value={view.showUnitOfMeasureSection}>
                        <PdfScheduleMetadataSection
                            originalUnitOfMeasure={view.original.unitOfMeasure}
                            draftUnitOfMeasure={view.draft.unitOfMeasure}
                            note={view.unitOfMeasureNote}
                            changed={view.unitOfMeasureChanged}
                        />
                    </If>
                    <If value={view.showIntroSection}>
                        <PdfRichTextSection
                            title="Introduction"
                            originalHtml={originalIntroHtml}
                            draftHtml={draftIntroHtml}
                            note={view.amendsByKey["intro.content"]}
                            changed={view.introChanged}
                            pageBreakAfter={view.showNotesSection ? "auto" : "page"}
                        />
                    </If>

                    <If value={view.showNotesSection}>
                        <PdfRichTextSection
                            title="Notes"
                            originalHtml={originalNoteHtml}
                            draftHtml={draftNoteHtml}
                            note={view.amendsByKey["intro.note"]}
                            changed={view.noteChanged}
                            pageBreakAfter="page"
                        />
                    </If>

                    <Repeat
                        list={view.sideBySideTaskRows}
                        item={<PdfTaskComparisonSection showUnchangedNote={true} />}
                    />
                    <PdfReferencesSection section={view.referencesSection} />
                    <PdfTaskOrderChangesSection section={view.taskOrderChangesSection} />
                </PdfComparisonTable>
            </Box>
        </>
    )
}
